/**
 * Fullstory Privacy Wrapper Components
 *
 * These wrapper components apply Fullstory privacy classes using display: contents,
 * making them transparent in the layout while ensuring the privacy class is always applied.
 *
 * Use these when:
 * - A component might strip the className prop
 * - You want to ensure privacy is applied regardless of component behavior
 * - You need a guaranteed wrapper around sensitive content
 *
 * Example:
 * <FullStoryExclude>
 *   <SomeComponentThatMightStripClassName value={email} />
 * </FullStoryExclude>
 */

import { forwardRef, ElementType, HTMLAttributes, ReactNode } from 'react';
import { FS_CLASSES, PRIVACY_LEVELS } from './constants';
import { getRecommendedPrivacyLevel } from './helpers';

/**
 * Base wrapper style using display: contents
 * This makes the wrapper transparent in the DOM layout while preserving the privacy class
 */
const wrapperCss = "w19rujuq";

/**
 * Props for privacy wrapper components
 */
interface PrivacyWrapperProps extends HTMLAttributes<HTMLElement> {
  /** Element type to render (default: 'span') */
  as?: ElementType;
  /** Child elements to wrap */
  children?: ReactNode;
}

/**
 * FullStoryExclude - Completely blocks Fullstory recording
 *
 * Wraps content in a span with fs-exclude class and display: contents.
 * The element and its children will appear as blank boxes in Fullstory recordings.
 *
 * @example
 * <FullStoryExclude>
 *   <TextField name="password" type="password" />
 * </FullStoryExclude>
 *
 * @example
 * // With custom element type
 * <FullStoryExclude as="div">
 *   <CreditCardInput />
 * </FullStoryExclude>
 */
export const FullStoryExclude = forwardRef<HTMLElement, PrivacyWrapperProps>(({
  as: Component = 'span',
  children,
  ...props
}, ref) => <Component ref={ref} {...props} className={`${wrapperCss} ${FS_CLASSES.EXCLUDE} ${props.className || ''}`.trim()}>
      {children}
    </Component>);
FullStoryExclude.displayName = 'FullStoryExclude';

/**
 * FullStoryMask - Masks content with asterisks while preserving structure
 *
 * Wraps content in a span with fs-mask class and display: contents.
 * The structure is recorded but text/values are replaced with asterisks in Fullstory.
 *
 * @example
 * <FullStoryMask>
 *   <span>{user.email}</span>
 * </FullStoryMask>
 *
 * @example
 * // Masking displayed contact info
 * <FullStoryMask>
 *   <ContactCard email={email} phone={phone} />
 * </FullStoryMask>
 */
export const FullStoryMask = forwardRef<HTMLElement, PrivacyWrapperProps>(({
  as: Component = 'span',
  children,
  ...props
}, ref) => <Component ref={ref} {...props} className={`${wrapperCss} ${FS_CLASSES.MASK} ${props.className || ''}`.trim()}>
      {children}
    </Component>);
FullStoryMask.displayName = 'FullStoryMask';

/**
 * FullStoryUnmask - Explicitly allows Fullstory recording
 *
 * Wraps content in a span with fs-unmask class and display: contents.
 * Useful for ensuring specific sections are recorded when under a masked parent.
 *
 * @example
 * <FullStoryMask>
 *   <div>
 *     <span>{sensitiveData}</span>
 *     <FullStoryUnmask>
 *       <button>Public Action</button>
 *     </FullStoryUnmask>
 *   </div>
 * </FullStoryMask>
 */
export const FullStoryUnmask = forwardRef<HTMLElement, PrivacyWrapperProps>(({
  as: Component = 'span',
  children,
  ...props
}, ref) => <Component ref={ref} {...props} className={`${wrapperCss} ${FS_CLASSES.UNMASK} ${props.className || ''}`.trim()}>
      {children}
    </Component>);
FullStoryUnmask.displayName = 'FullStoryUnmask';

/**
 * Props for FullStoryAutoMask component
 */
interface AutoMaskProps extends PrivacyWrapperProps {
  /** Content to analyze for PII patterns (email, phone) */
  content?: string;
}

/**
 * Conditional wrapper that applies privacy based on content detection
 *
 * Automatically wraps content with FullStoryMask if PII patterns are detected.
 * Useful for dynamic content where you're not sure if masking is needed.
 *
 * @example
 * <FullStoryAutoMask content={dynamicText}>
 *   <span>{dynamicText}</span>
 * </FullStoryAutoMask>
 */
export const FullStoryAutoMask = forwardRef<HTMLElement, AutoMaskProps>(({
  as = 'span',
  children,
  content,
  ...props
}, ref) => {
  const needsMask = getRecommendedPrivacyLevel(content) === PRIVACY_LEVELS.MASK;
  if (needsMask) {
    return <FullStoryMask as={as} ref={ref} {...props}>
          {children}
        </FullStoryMask>;
  }
  const Component = as;
  return <Component ref={ref} {...props}>
        {children}
      </Component>;
});
FullStoryAutoMask.displayName = 'FullStoryAutoMask';

require("./components.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./components.tsx");